import { ReviewStatus } from "@/enums";
import { NotificationTypeEnum } from "@/enums/notificationEnum";
import i18n from "@/locales";
import { Mate, Notification } from "@/types/entity";
import {
  AdmireNotifyData,
  AdmireReplyNotifyData,
  AtInMomentNotifyData,
  ChatMessageEarnedNotifyData,
  ChatNotifyData,
  CoinVoteNotifyData,
  CollectionInvitedNotifyData,
  CommitNotifyData,
  CommitReviewNotifyData,
  DonateNotifyData,
  EmojiNotifyData,
  FollowOrAdmireNotifyData,
  HouseEventGeneratedNotifyData,
  ImageTaskNotifyData,
  MateAdmireSubscribedNotifyData,
  MateAdoptedNotifyData,
  MateGeneratedNotifyData,
  MateInvitationNotifyData,
  MateMailNotifyData,
  MediaPurchasedNotifyData,
  MemberSubscriptionReceivedNotifyData,
  NewSiteMessageNotifyData,
  NotifyData,
  PlainNotificationItem,
  ReplyNotifyData,
  SpriteGeneratedNotifyData,
  StoryGenNotifyData,
  StoryVideoNotifyData,
  TextImageEmojiNotifyData,
  TextImageRepliedNotifyData,
  UserShareRewardNotifyData,
  UserTaskCompletedNotifyData,
  WishNotifyData,
  WsNotification,
} from "@/types/notification";
import { getMemberTypeByPlanId } from "@/views/Crystal/memberData";
import md5 from "crypto-js/md5";

/**
 * @file
 * 这个文件中不能引用任何 Vue 相关的内容，因为这个文件可能在 Service Worker 中执行
 */

function fromEmoji(n: WsNotification<EmojiNotifyData>): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    title: i18n.global.t("Notifications.type.likeMoment", {
      mateName: n.data.mate.name,
    }),
    avatarUrl: n.data.mate.mediaUrl,
    content: n.data.content,
    path: `/thread/${n.data.momentUuid}`,
    time: n.dtCreate ?? "",
  };
}

function fromImageTask(
  n: WsNotification<ImageTaskNotifyData>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    avatarUrl: n.data.mediaUrl,
    title: i18n.global.t("Notifications.type.imageGenerationComplete"),
    path: "/generation/image/list",
    time: n.dtCreate ?? "",
  };
}

function fromReply(n: WsNotification<ReplyNotifyData>): PlainNotificationItem {
  let title = "";
  const { id, type, data } = n;
  if (n.data.momentType === 2) {
    title = data.mate.name + " " + i18n.global.t(`Message.responseYou`);
  } else if (n.type == NotificationTypeEnum.ReplyMentionedMateInMoment) {
    title = i18n.global.t("Notifications.type.replyMentioned", {
      mateName: data.mate.name,
    });
  } else if (n.type === NotificationTypeEnum.MomentCommented) {
    title = i18n.global.t("Notifications.type.repliedMoment", {
      mateName: data.mate.name,
    });
  } else if (n.type === NotificationTypeEnum.MomentReplied) {
    title = i18n.global.t("Notifications.type.repliedComment", {
      mateName: data.mate.name,
    });
  }

  const avatarUrl = data.mate?.mediaUrl;

  const path = n.data.momentType === 1 ? `/thread` : `/treehole`;

  return {
    title,
    avatarUrl,
    id,
    type,
    content: n.data.content,
    path: `${path}/${n.data.momentUuid}#${n.data.replyId}`,
    time: n.dtCreate ?? "",
  };
}

function fromTextImageReply(
  n: WsNotification<TextImageRepliedNotifyData>
): PlainNotificationItem {
  let title = "";
  const { id, type, data } = n;
  if (n.type == NotificationTypeEnum.ReplyMentionedMateInTextImage) {
    title = i18n.global.t("Notifications.type.replyMentioned", {
      mateName: data.mate.name,
    });
  } else if (n.type === NotificationTypeEnum.TextImageCommented) {
    title = i18n.global.t("Notifications.type.repliedTextImage", {
      mateName: data.mate.name,
    });
  } else if (n.type === NotificationTypeEnum.TextImageReplied) {
    title = i18n.global.t("Notifications.type.repliedComment", {
      mateName: data.mate.name,
    });
  } else if (n.type === NotificationTypeEnum.WishCompleted) {
    title = i18n.global.t("Notifications.type.WishCompleted");
  }

  const avatarUrl = data.mate?.mediaUrl;

  return {
    title,
    avatarUrl,
    id,
    type,
    content: n.data.content,
    path: `/text-image-explore?uuid=${n.data.textImageUuid}`,
    time: n.dtCreate ?? "",
  };
}

function fromTextImageEmoji(
  n: WsNotification<TextImageEmojiNotifyData>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    title: i18n.global.t("Notifications.type.likeTextImage", {
      mateName: n.data.mate.name,
    }),
    avatarUrl: n.data.mate.mediaUrl,
    content: n.data.content,
    path: `/text-image-explore?uuid=${n.data.textImageUuid}`,
    time: n.dtCreate ?? "",
  };
}

function fromDonate(
  n: WsNotification<DonateNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { giver, amount, text } = data;
  const { name, mediaUrl } = giver;
  const title = i18n.global.t("Notifications.type.donate", {
    giver: name,
    amount: amount,
  });
  return {
    title,
    avatarUrl: mediaUrl,
    id,
    type,
    content: text,
    path: "/transaction",
    time: n.dtCreate ?? "",
  };
}

function fromCollectionInvited(
  n: WsNotification<CollectionInvitedNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { collectionUuid, mate } = data;
  const { name, mediaUrl } = mate;
  const title = i18n.global.t("Notifications.type.collectionInvited", {
    name,
  });
  return {
    title,
    avatarUrl: mediaUrl,
    id,
    type,
    content: "",
    path: `/collection/${collectionUuid}`,
    time: n.dtCreate ?? "",
  };
}

function fromCoinVote(
  n: WsNotification<CoinVoteNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  let giver = "";
  let avatarUrl = "";
  if (data.mate) {
    const { name, mediaUrl } = data.mate;
    giver = name;
    avatarUrl = mediaUrl;
  } else {
    giver = `${i18n.global.t("Coin.anonymousUser")}-${md5(
      data.userId!.toString()
    )
      .toString()
      .slice(0, 8)}`;
  }
  const title = i18n.global.t("Notifications.type.coinVote", {
    giver,
  });
  return {
    title,
    avatarUrl,
    id,
    type,
    path: "/rank/vote",
    content: data.text ?? "",
    time: n.dtCreate ?? "",
  };
}

function fromBecomeSeniorReader(
  n: WsNotification<void>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    title: i18n.global.t("Notifications.system"),
    content: i18n.global.t("Notifications.type.becomeSeniorReader"),
    avatarUrl: "",
    path: "/goldTicket/balance",
    time: n.dtCreate ?? "",
  };
}

function fromCommit(
  n: WsNotification<CommitNotifyData>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    title: i18n.global.t("Notifications.type.createContentForYou", {
      mateName: n.data.mate.name,
    }),
    avatarUrl: n.data.mate.mediaUrl,
    content: n.data.title,
    path: "/request/list",
    time: n.dtCreate ?? "",
  };
}

function fromChat(n: WsNotification<ChatNotifyData>): PlainNotificationItem {
  const { mate, text, mateSessionId } = n.data;
  return {
    id: n.id,
    type: n.type,
    title: i18n.global.t("Notifications.type.messageFrom", {
      mateName: mate.name,
    }),
    avatarUrl: n.data.mate.mediaUrl,
    content: text,
    path: `/chat/${mateSessionId}/${mate.uuid}`,
    time: n.dtCreate ?? "",
  };
}

function fromFollowOrAdmire(
  n: WsNotification<FollowOrAdmireNotifyData>
): PlainNotificationItem {
  const { mate } = n.data;
  return {
    id: n.id,
    type: n.type,
    title: `${mate.name} ${
      n.type === NotificationTypeEnum.NewFollower
        ? i18n.global.t("Notifications.type.followedYou")
        : i18n.global.t("Notifications.type.guardedYou")
    }`,
    avatarUrl: n.data.mate.mediaUrl,
    path: `/social/list#${
      n.type === NotificationTypeEnum.NewFollower ? "follower" : "admirer"
    }`,
    time: n.dtCreate ?? "",
  };
}

function fromCommitReview(
  n: WsNotification<CommitReviewNotifyData>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    avatarUrl: n.data.mate.mediaUrl,
    title: `${n.data.mate.name} ${
      n.data.status === 2
        ? i18n.global.t("Notifications.type.approvedContentCreated")
        : i18n.global.t("Notifications.type.declinedContentCreated")
    } `,
    path: `/request/list`,
    time: n.dtCreate ?? "",
  };
}

function fromStoryGen(
  n: WsNotification<StoryGenNotifyData>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    avatarUrl: n.data.mediaUrl ?? undefined,
    title: i18n.global.t("Notifications.type.storyGenerated", {
      title: n.data.title,
    }),
    path: `/story-book/${n.data.storyBookUuid}`,
    time: n.dtCreate ?? "",
  };
}

function fromStoryVideo(
  n: WsNotification<StoryVideoNotifyData>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    avatarUrl: n.data.mediaUrl ?? undefined,
    title: i18n.global.t("Notifications.type.storyVideoGenerated", {
      title: n.data.title,
    }),
    path: "/generation/image/list#toMark",
    time: n.dtCreate ?? "",
  };
}

function fromAtInMoment(
  n: WsNotification<AtInMomentNotifyData>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    avatarUrl: n.data.mate.mediaUrl ?? undefined,
    title: i18n.global.t("Notifications.type.mentionedMoment", {
      mateName: n.data.mate.name,
    }),
    path: `/thread/${n.data.momentUuid}`,
    content: n.data.text,
    time: n.dtCreate ?? "",
  };
}

function fromAdmire(
  n: WsNotification<AdmireNotifyData>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    avatarUrl: n.data.sender.mediaUrl ?? undefined,
    title: i18n.global.t("Notifications.type.wantGuardYou", {
      mateName: n.data.sender.name,
    }),
    path: "/request/list",
    time: n.dtCreate ?? "",
  };
}

function fromAdmireResponse(
  n: WsNotification<AdmireReplyNotifyData>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    avatarUrl: n.data.receiver.mediaUrl ?? undefined,
    title: `${n.data.receiver.name} ${
      n.data.status === 3
        ? i18n.global.t("Notifications.type.declinedGuardianRequest")
        : i18n.global.t("Notifications.type.agreedGuardianRequest")
    }`,
    path: "/social/list#admiring",
    time: n.dtCreate ?? "",
  };
}

const admireTypeMap: Record<number, string> = {
  5: "superAdmirePending",
  6: "superAdmireSuccess",
  7: "superAdmireFailed",
};

function fromSuperAdmireResult(
  n: WsNotification<{ status: number; mate?: Mate }>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { status, mate } = data;
  const title = i18n.global.t(`Notifications.type.${admireTypeMap[status]}`);

  return {
    title,
    avatarUrl: mate?.mediaUrl || "",
    id,
    type,
    content: "",
    path: mate ? `/mate/${mate.uuid}` : "",
    time: n.dtCreate ?? "",
  };
}

function fromMediaPurchasedNotifyData(
  n: WsNotification<MediaPurchasedNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { purchaser, totalPrice } = data;
  const { name, uuid, mediaUrl } = purchaser;
  const title = i18n.global.t("Notifications.type.mediaPurchased", {
    name,
    amount: totalPrice,
  });

  return {
    title,
    avatarUrl: mediaUrl || "",
    id,
    type,
    content: "",
    path: `/mate/${uuid}`,
    time: n.dtCreate ?? "",
  };
}

function fromMateAdmireSubscribed(
  n: WsNotification<MateAdmireSubscribedNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { price, admirer } = data;
  const { name, uuid, mediaUrl } = admirer;
  const title = i18n.global.t("Notifications.type.mateAdmireSubscribed", {
    name,
    amount: price,
  });

  return {
    title,
    avatarUrl: mediaUrl || "",
    id,
    type,
    content: "",
    path: `/mate/${uuid}`,
    time: n.dtCreate ?? "",
  };
}

function fromChatMessageEarned(
  n: WsNotification<ChatMessageEarnedNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  let avatarUrl = "";
  const { name, mediaUrl } = data.mate;
  avatarUrl = mediaUrl;
  const title = i18n.global.t("Notifications.type.ChatMessageEarned", {
    name,
    amount: data.receivedAmount,
  });
  return {
    title,
    avatarUrl,
    id,
    type,
    path: "/transaction",
    time: n.dtCreate ?? "",
  };
}

function fromDispatchMissionReturn(
  n: WsNotification<ReplyNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  let avatarUrl = "";
  const { mate, uuid } = data;
  const { name, mediaUrl } = mate;
  avatarUrl = mediaUrl;
  const title = i18n.global.t("Notifications.type.DispatchMissionReturn", {
    name,
  });
  return {
    title,
    avatarUrl,
    id,
    type,
    path: `/mission/detail?dispatchUuid=${uuid}`,
    time: n.dtCreate ?? "",
  };
}

function fromMateWishCompleted(
  n: WsNotification<WishNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { uuid, imageMediaUrl } = data;
  const title = i18n.global.t("Notifications.type.WishCompleted");

  return {
    title,
    avatarUrl: imageMediaUrl,
    id,
    type,
    path: "/mateWish/detail/" + uuid,
    time: n.dtCreate ?? "",
  };
}

function fromMateAdopted(
  n: WsNotification<MateAdoptedNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { mate } = data;
  const { name, uuid, mediaUrl } = mate;
  const title = i18n.global.t("Notifications.type.mateAdopted", {
    name,
  });
  return {
    title,
    avatarUrl: mediaUrl,
    id,
    type,
    path: `/mate/${uuid}`,
    time: n.dtCreate ?? "",
  };
}

function fromNewSiteMessage(
  n: WsNotification<NewSiteMessageNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { title, uuid } = data;
  return {
    title,
    avatarUrl: "",
    id,
    type,
    path: uuid,
    time: n.dtCreate ?? "",
  };
}

function fromUserShareReward(
  n: WsNotification<UserShareRewardNotifyData>
): PlainNotificationItem {
  const { reviewStatus, powerCubes } = n.data;

  const title = i18n.global.t(`Notifications.type.userShareReward`, {
    status: i18n.global.t(`Notifications.userShareReward.${reviewStatus}`),
  });

  const content =
    reviewStatus === ReviewStatus.Approved
      ? i18n.global.t(`Notifications.userShareReward.approveView`, {
          powerCubes,
        })
      : i18n.global.t(`Notifications.userShareReward.rejectView`);
  return {
    title,
    avatarUrl: "",
    id: n.id,
    type: n.type,
    path: "",
    time: n.dtCreate ?? "",
    content,
  };
}

function fromMateMail(
  n: WsNotification<MateMailNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { mate, subject, uuid } = data;
  const { name, mediaUrl } = mate;

  const title = i18n.global.t("Notifications.type.mateMail", {
    name,
  });

  return {
    title,
    avatarUrl: mediaUrl,
    path: `/mail/detail/${uuid}`,
    time: n.dtCreate ?? "",
    content: subject,
    id,
    type,
  };
}

function fromMateInvitation(
  n: WsNotification<MateInvitationNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { mate, title: content } = data;
  const { name, mediaUrl } = mate;
  const title = i18n.global.t("Notifications.type.mateInvitation", {
    name,
  });
  return {
    title,
    avatarUrl: mediaUrl,
    id,
    type,
    path: `/house/interaction/review`,
    time: n.dtCreate ?? "",
    content,
  };
}

function fromHouseEventGenerated(
  n: WsNotification<HouseEventGeneratedNotifyData>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    title: i18n.global.t("Notifications.type.houseEventGenerated", {
      name: n.data.mate.name,
    }),
    avatarUrl: n.data.mate.mediaUrl,
    path: `/house?tab=events`,
    time: n.dtCreate ?? "",
  };
}

function fromSpriteGenerated(
  n: WsNotification<SpriteGeneratedNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { mate, id: spriteId, uuid: spriteUuid } = data;
  const { name, mediaUrl, uuid: mateUuid } = mate;
  const title = i18n.global.t("Notifications.type.spriteGenerated", {
    name,
  });

  return {
    title,
    avatarUrl: mediaUrl,
    id,
    type,
    path: `/mate/create?spriteId=${spriteId}`,
    time: n.dtCreate ?? "",
    originData: {
      spriteUuid,
      spriteId,
      mateUuid,
    },
  };
}

function fromMateGenerated(
  n: WsNotification<MateGeneratedNotifyData>
): PlainNotificationItem {
  return {
    id: n.id,
    type: n.type,
    title: i18n.global.t("Notifications.type.mateGenerated", {
      name: n.data.mate.name,
    }),
    avatarUrl: n.data.mate.mediaUrl,
    path: `/mate/${n.data.mate.uuid}`,
    time: n.dtCreate ?? "",
  };
}

function fromMemberSubscriptionReceived(
  n: WsNotification<MemberSubscriptionReceivedNotifyData>
): PlainNotificationItem {
  const { data, id, type } = n;
  const { subscription } = data;
  const memberType = getMemberTypeByPlanId(subscription.planId) || 0;

  const title = i18n.global.t("Notifications.type.memberSubscriptionReceived", {
    name: i18n.global.t(`User.memberType.${memberType}`),
  });
  return {
    id,
    type,
    title,
    avatarUrl: "/img/energy-box-4.png",
    time: n.dtCreate ?? "",
    path: "#",
    originData: {
      subscription,
    },
  };
}

function fromUserTaskCompleted(
  n: WsNotification<UserTaskCompletedNotifyData>
): PlainNotificationItem {
  const { task, mate } = n.data;
  const { title: taskTitle } = task;
  const { mediaUrl } = mate;
  const title = i18n.global.t("Notifications.type.userTaskCompleted", {
    name: taskTitle,
  });
  return {
    title,
    avatarUrl: mediaUrl,
    id: n.id,
    type: n.type,
    path: `/user/task`,
    time: n.dtCreate ?? "",
  };
}

export function plainNotificationItemFactory(
  n: Notification<NotifyData> | WsNotification<NotifyData>
): PlainNotificationItem {
  switch (n.type) {
    case NotificationTypeEnum.MomentReacted:
      return fromEmoji(n as WsNotification<EmojiNotifyData>);
    case NotificationTypeEnum.TextImageFinished:
      return fromImageTask(n as WsNotification<ImageTaskNotifyData>);
    case NotificationTypeEnum.MomentCommitRequested:
      return fromCommit(n as WsNotification<CommitNotifyData>);
    case NotificationTypeEnum.NewChatMessage:
      return fromChat(n as WsNotification<ChatNotifyData>);
    case NotificationTypeEnum.NewFollower:
    case NotificationTypeEnum.NewAdmirer:
      return fromFollowOrAdmire(n as WsNotification<FollowOrAdmireNotifyData>);
    case NotificationTypeEnum.MomentCommitReviewed:
      return fromCommitReview(n as WsNotification<CommitReviewNotifyData>);
    case NotificationTypeEnum.StoryGenerated:
      return fromStoryGen(n as WsNotification<StoryGenNotifyData>);
    case NotificationTypeEnum.VideoCreated:
      return fromStoryVideo(n as WsNotification<StoryVideoNotifyData>);
    case NotificationTypeEnum.MomentMentionedMate:
      return fromAtInMoment(n as WsNotification<AtInMomentNotifyData>);
    case NotificationTypeEnum.MateAdmireRequested:
      return fromAdmire(n as WsNotification<AdmireNotifyData>);
    case NotificationTypeEnum.MateAdmireReviewed:
      return fromAdmireResponse(n as WsNotification<AdmireReplyNotifyData>);
    case NotificationTypeEnum.TipReceived:
      return fromDonate(n as WsNotification<DonateNotifyData>);
    case NotificationTypeEnum.CoinVote:
      return fromCoinVote(n as WsNotification<CoinVoteNotifyData>);
    case NotificationTypeEnum.ReviewerPetition:
      return fromBecomeSeniorReader(n as WsNotification<never>);
    case NotificationTypeEnum.CollectionParticipantAdded:
      return fromCollectionInvited(
        n as WsNotification<CollectionInvitedNotifyData>
      );
    case NotificationTypeEnum.SuperAdmireReviewed:
      return fromSuperAdmireResult(n as WsNotification<{ status: number }>);
    case NotificationTypeEnum.MediaPurchased:
      return fromMediaPurchasedNotifyData(
        n as WsNotification<MediaPurchasedNotifyData>
      );
    case NotificationTypeEnum.MateAdmireSubscribed:
      return fromMateAdmireSubscribed(
        n as WsNotification<MateAdmireSubscribedNotifyData>
      );
    case NotificationTypeEnum.TextImageCommented:
    case NotificationTypeEnum.TextImageReplied:
    case NotificationTypeEnum.ReplyMentionedMateInTextImage:
      return fromTextImageReply(
        n as WsNotification<TextImageRepliedNotifyData>
      );
    case NotificationTypeEnum.TextImageReacted:
      return fromTextImageEmoji(n as WsNotification<TextImageEmojiNotifyData>);
    case NotificationTypeEnum.ChatMessageEarned:
      return fromChatMessageEarned(
        n as WsNotification<ChatMessageEarnedNotifyData>
      );

    case NotificationTypeEnum.DispatchMissionReturn:
      return fromDispatchMissionReturn(n as WsNotification<ReplyNotifyData>);
    case NotificationTypeEnum.WishCompleted:
      return fromMateWishCompleted(n as WsNotification<WishNotifyData>);
    case NotificationTypeEnum.MateAdopted:
      return fromMateAdopted(n as WsNotification<MateAdoptedNotifyData>);
    case NotificationTypeEnum.NewSiteMessage:
      return fromNewSiteMessage(n as WsNotification<NewSiteMessageNotifyData>);
    case NotificationTypeEnum.UserShareReward:
      return fromUserShareReward(
        n as WsNotification<UserShareRewardNotifyData>
      );
    case NotificationTypeEnum.MateMail:
      return fromMateMail(n as WsNotification<MateMailNotifyData>);
    case NotificationTypeEnum.MateInvitation:
      return fromMateInvitation(n as WsNotification<MateInvitationNotifyData>);
    case NotificationTypeEnum.HouseEventGenerated:
      return fromHouseEventGenerated(
        n as WsNotification<HouseEventGeneratedNotifyData>
      );
    case NotificationTypeEnum.SpriteGenerated:
      return fromSpriteGenerated(
        n as WsNotification<SpriteGeneratedNotifyData>
      );
    case NotificationTypeEnum.MateGenerated:
      return fromMateGenerated(n as WsNotification<MateGeneratedNotifyData>);
    case NotificationTypeEnum.MemberSubscriptionReceived:
      return fromMemberSubscriptionReceived(
        n as WsNotification<MemberSubscriptionReceivedNotifyData>
      );
    case NotificationTypeEnum.UserTaskCompleted:
      return fromUserTaskCompleted(
        n as WsNotification<UserTaskCompletedNotifyData>
      );
    case NotificationTypeEnum.MomentCommented:
    case NotificationTypeEnum.MomentReplied:
    case NotificationTypeEnum.ReplyMentionedMateInMoment:
    default:
      return fromReply(n as WsNotification<ReplyNotifyData>);
  }
}
